/**
 * The NeuronRegistry claim index, read straight from Arc through the public RPC.
 *
 * The rows are the registry's own `NeuronRegistered` events - the contract IS an
 * index (one row per hatched neuron, ever), so the log scan is the complete
 * chain-wide claim list, not a per-neuron guess against whatever neurons some
 * local indexer happens to have scanned yet. Rows are UNVERIFIED CLAIMS
 * (contract natspec + ROW_LABEL): the event pairs (neuronId, thoughtTxHash,
 * token), and this module verifies the fired half against the thought's own
 * receipt before handing a claim over as a specimen.
 *
 * Arc caps eth_getLogs at ~10k blocks (-32012) and throttles bursts (-32005),
 * so the scan walks 9,999-block ranges with spacing and backoff - and only
 * trusts its own result when the chain's own `totalRegistered` count agrees.
 */
import { Contract, getAddress, JsonRpcProvider } from "ethers";

import {
  asArray,
  asNumber,
  asString,
  field,
  optionalField,
  parseJson,
  type JsonValue,
} from "./json";

import { BRAIN_STATE_ABI, decodeBrainState, type Thought } from "../../launchpad/specimen-core.mjs";

export const REGISTRY_ADDRESS = "0x72cdc3C2A60B18B8638a0d7092581d4f2847A944";

export const ARC_RPC = "https://rpc.mainnet.arc.io";

/** The registry's deploy block on Arc mainnet: where its event history begins. */
export const REGISTRY_DEPLOY_BLOCK = 21_688_118;

const ABI = [
  "event NeuronRegistered(uint256 indexed neuronId, bytes32 indexed thoughtTxHash, address token)",
  "function totalRegistered() view returns (uint256)",
];

export type RegistryClaim = {
  readonly neuron: number;
  readonly thoughtTxHash: string;
  readonly token: string;
  /** The block the claim was written in: the registry's own hatch order. */
  readonly block: number;
  /**
   * The indexer's own proof of this claim: the thought the claim names, from the
   * BrainState row the indexer decoded, kept only when that thought fired this
   * neuron. A row carrying it is already verified and skips the node read; a row
   * without it (an indexer that served the raw claim, or one that no longer keeps
   * that old tick) is verified against the thought's receipt instead.
   */
  readonly thought?: ClaimThought;
};

/**
 * The thought a claim names, in the shape the launchpad core derives a genome
 * from. It is decoded from the thought transaction's own BrainState event -
 * the chain's copy, not a local indexer's decoded row.
 */
export type ClaimThought = Thought;

/** One range of the log scan, in the order the registry wrote it. */
export type ClaimLog = {
  readonly neuron: number;
  readonly thoughtTxHash: string;
  readonly token: string;
  readonly block: number;
};

/** The chain reads one scan round needs, injectable so the policy tests without a node. */
export type RegistryChain = {
  readonly total: () => Promise<number>;
  readonly head: () => Promise<number>;
  readonly logs: (fromBlock: number, toBlock: number) => Promise<readonly ClaimLog[]>;
};

export type RegistryReadOptions = {
  readonly chain?: RegistryChain;
  /** The indexer's own rows; null when it does not serve them. Injectable for tests. */
  readonly served?: () => Promise<Map<number, RegistryClaim> | null>;
  readonly cache?: ClaimCache | null;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly spacingMs?: number;
  readonly attempts?: number;
  readonly chunk?: number;
};

/** A completed scan, kept so a visitor does not re-read the whole history per visit. */
export type ClaimCache = {
  readonly total: number;
  readonly claims: readonly RegistryClaim[];
};

const CHUNK = 9_999;

const RANGE_SPACING_MS = 1_500;

const RANGE_ATTEMPTS = 5;

/** The localStorage key holding the last complete scan. */
export const CLAIM_CACHE_KEY = "od-obrain-registry";

/** One provider for the whole page: a per-read provider would re-handshake the node. */
let arc: JsonRpcProvider | undefined;

function provider(): JsonRpcProvider {
  arc ??= new JsonRpcProvider(ARC_RPC, undefined, { staticNetwork: true, batchMaxCount: 1 });

  return arc;
}

let contract: Contract | undefined;

function registry(): Contract {
  contract ??= new Contract(REGISTRY_ADDRESS, ABI, provider());

  return contract;
}

const ARC_CHAIN: RegistryChain = {
  total: async (): Promise<number> => Number(await registry().totalRegistered()),

  head: async (): Promise<number> => provider().getBlockNumber(),

  logs: async (fromBlock: number, toBlock: number): Promise<readonly ClaimLog[]> => {
    const contract = registry();
    const event = contract.interface.getEvent("NeuronRegistered");

    if (event === null) throw new Error("the registry ABI cannot read NeuronRegistered");

    const logs = await provider().getLogs({
      address: REGISTRY_ADDRESS,
      topics: [event.topicHash],
      fromBlock,
      toBlock,
    });

    return logs.map((log) => {
      const parsed = contract.interface.parseLog({ topics: [...log.topics], data: log.data });

      if (parsed === null) throw new Error(`undecodable registry log at block ${log.blockNumber}`);

      return {
        neuron: Number(parsed.args.neuronId),
        thoughtTxHash: String(parsed.args.thoughtTxHash),
        token: String(parsed.args.token),
        block: Number(log.blockNumber),
      };
    });
  },
};

function sleepMs(ms: number): Promise<void> {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

/** One range, retried while the node refuses to answer: an answered range is final. */
async function logsRetried(
  chain: RegistryChain,
  fromBlock: number,
  toBlock: number,
  sleep: (ms: number) => Promise<void>,
  attempts: number,
  spacingMs: number,
): Promise<readonly ClaimLog[]> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await chain.logs(fromBlock, toBlock);
    } catch (error) {
      if (attempt >= attempts - 1) throw error;

      await sleep(spacingMs * 2 ** attempt);
    }
  }
}

/**
 * The indexer's own registry rows, or null when that route is not there.
 *
 * The indexer walks the registry's log once, from its deploy block, and keeps
 * every row in its sqlite: a visitor's collection is then one local HTTP read
 * instead of a chain scan. A node or an indexer old enough to predate the route
 * answers something else, and the scan below is what keeps the page working.
 */
async function indexerClaims(): Promise<Map<number, RegistryClaim> | null> {
  try {
    const response = await fetch("/api/registry", { headers: { accept: "application/json" } });

    if (!response.ok) return null;

    const body = parseJson(await response.text(), "/api/registry");
    const claims = asArray(field(body, "claims", "/api/registry"), "/api/registry");

    if (claims.length === 0) return null;

    return new Map(
      claims.map((claim) => {
        const row = cachedClaim(claim, "/api/registry");

        // the indexer serves the address as the chain stores it; the checksummed
        // form is what every other surface of the page shows, so claim it here
        return [row.neuron, { ...row, token: getAddress(row.token) }];
      }),
    );
  } catch {
    // not an indexer that serves the registry: the chain scan is the read
    return null;
  }
}

export type ClaimProof = {
  /** claims the indexer proved, by neuron: these skip the node read */
  readonly proven: Map<number, ClaimThought>;
  readonly unproven: readonly RegistryClaim[];
};

/**
 * The claims the indexer already proved, split from those that still need the node.
 *
 * The proof is the contract's own rule - the thought must fire the neuron the
 * claim names - and a card is only ever built from a thought that passes it, so a
 * served thought that does not is treated as unproven rather than trusted.
 */
export function provenThoughts(claims: readonly RegistryClaim[]): ClaimProof {
  const proven = new Map<number, ClaimThought>();
  const unproven: RegistryClaim[] = [];

  for (const claim of claims) {
    if (claim.thought !== undefined && claim.thought.firedIds.includes(claim.neuron))
      proven.set(claim.neuron, claim.thought);
    else unproven.push(claim);
  }

  return { proven, unproven };
}

/**
 * Every claim the registry has ever written.
 *
 * The indexer's rows first (they are already local), and the chain itself when
 * that route is unavailable. The walk is newest-first and stops as soon as it
 * holds `totalRegistered` rows, which is the contract's own proof that nothing
 * is missing above it. A complete walk is cached and reused while that count is
 * unchanged (rows are one-shot and never removed, so a matching count cannot
 * hide a row); an incomplete one is returned as-is and never cached, so the
 * next visit retries the ranges the node refused.
 */
export async function registryClaims(
  options: RegistryReadOptions = {},
): Promise<Map<number, RegistryClaim>> {
  const served = await (options.served ?? indexerClaims)();

  if (served !== null) return served;

  const chain = options.chain ?? ARC_CHAIN;
  const sleep = options.sleep ?? sleepMs;
  const spacingMs = options.spacingMs ?? RANGE_SPACING_MS;
  const attempts = options.attempts ?? RANGE_ATTEMPTS;
  const chunk = options.chunk ?? CHUNK;
  const cache = options.cache === undefined ? readClaimCache() : options.cache;

  const total = await chain.total();

  if (cache !== null && cache.total === total)
    return new Map(cache.claims.map((claim) => [claim.neuron, claim]));

  const head = await chain.head();
  const claims = new Map<number, RegistryClaim>();
  let complete = true;

  for (let to = head; to >= REGISTRY_DEPLOY_BLOCK; to -= chunk) {
    if (to < head) await sleep(spacingMs);

    try {
      for (const log of await logsRetried(
        chain,
        Math.max(REGISTRY_DEPLOY_BLOCK, to - chunk + 1),
        to,
        sleep,
        attempts,
        spacingMs,
      ))
        claims.set(log.neuron, log);
    } catch {
      // the node kept refusing this range: keep what answered, and do not
      // remember the result as complete
      complete = false;
      break;
    }

    if (claims.size >= total) break;
  }

  if (complete && claims.size >= total) writeClaimCache({ total, claims: [...claims.values()] });

  return claims;
}

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // private modes refuse storage; the scan then simply runs every visit
    return null;
  }
}

/** One cached row, decoded the way the live read produces it. */
function cachedClaim(value: JsonValue, label: string): RegistryClaim {
  const claim: RegistryClaim = {
    neuron: asNumber(field(value, "neuron", label), `${label}.neuron`),
    thoughtTxHash: asString(field(value, "thoughtTxHash", label), `${label}.thoughtTxHash`),
    token: asString(field(value, "token", label), `${label}.token`),
    block: asNumber(field(value, "block", label), `${label}.block`),
  };

  const thought = provenThought(value, label);

  return thought === undefined ? claim : { ...claim, thought };
}

/**
 * A thought the indexer proved, or undefined when the row carries none we can read.
 *
 * A served thought this client cannot read is dropped rather than thrown: the
 * claim itself is still a claim, and the receipt read verifies it the same way it
 * does every other one.
 */
function provenThought(value: JsonValue, label: string): ClaimThought | undefined {
  const thought = optionalField(value, "thought", label);

  if (thought === null) return undefined;

  try {
    const firedIds = asArray(field(thought, "firedIds", label), `${label}.firedIds`).map((id) =>
      asNumber(id, `${label}.firedIds`),
    );

    return {
      tx: asString(field(thought, "tx", label), `${label}.thought.tx`),
      feeder: asString(field(thought, "feeder", label), `${label}.thought.feeder`),
      tick: asNumber(field(thought, "tick", label), `${label}.thought.tick`),
      stateRoot: asString(field(thought, "stateRoot", label), `${label}.thought.stateRoot`),
      firedIds,
      synapses: asNumber(field(thought, "synapses", label), `${label}.thought.synapses`),
      spiked: asNumber(field(thought, "spiked", label), `${label}.thought.spiked`),
      potentials: asArray(field(thought, "potentials", label), `${label}.thought.potentials`).map(
        (value) => asString(value, `${label}.thought.potentials`),
      ),
    };
  } catch {
    return undefined;
  }
}

/**
 * The last complete scan, or null when there is none to trust.
 *
 * The stored JSON is input from outside this module, so it is decoded here and
 * nowhere else: a cache that does not decode is thrown away, and the page
 * scans again rather than rendering a half-read row.
 */
export function readClaimCache(storage: Storage | null = store()): ClaimCache | null {
  try {
    const raw = storage?.getItem(CLAIM_CACHE_KEY);

    if (raw === null || raw === undefined) return null;

    const parsed = parseJson(raw, CLAIM_CACHE_KEY);

    return {
      total: asNumber(field(parsed, "total", CLAIM_CACHE_KEY), `${CLAIM_CACHE_KEY}.total`),
      claims: asArray(field(parsed, "claims", CLAIM_CACHE_KEY), CLAIM_CACHE_KEY).map((claim) =>
        cachedClaim(claim, CLAIM_CACHE_KEY),
      ),
    };
  } catch {
    return null;
  }
}

function writeClaimCache(cache: ClaimCache, storage: Storage | null = store()): void {
  try {
    storage?.setItem(CLAIM_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // a full or refused store costs a rescan, never the render
  }
}

export type ThoughtReadOptions = {
  readonly sleep?: (ms: number) => Promise<void>;
  readonly spacingMs?: number;
  readonly attempts?: number;
  readonly read?: (claim: RegistryClaim) => Promise<ClaimThought | null>;
};

/** One receipt read: the thought the claim names, or null when the chain disproves it. */
async function readClaimThoughtOnce(
  claim: RegistryClaim,
  brain: string,
): Promise<ClaimThought | null> {
  const receipt = await provider().getTransactionReceipt(claim.thoughtTxHash);

  if (receipt === null) throw new Error(`no receipt for ${claim.thoughtTxHash}`);

  const topic = new Contract(REGISTRY_ADDRESS, BRAIN_STATE_ABI, provider()).interface.getEvent(
    "BrainState",
  )?.topicHash;

  const log = receipt.logs.find(
    (entry) => entry.address.toLowerCase() === brain.toLowerCase() && entry.topics[0] === topic,
  );

  if (log === undefined) return null;

  const decoded = decodeBrainState(log);

  // the contract's own rule: the thought tx receipt must fire this neuron
  if (!decoded.firedIds.includes(claim.neuron)) return null;

  return {
    tx: claim.thoughtTxHash,
    feeder: receipt.from,
    tick: decoded.tick,
    stateRoot: decoded.stateRoot,
    firedIds: decoded.firedIds,
    synapses: decoded.synapses,
    spiked: decoded.spiked,
    potentials: decoded.potentials,
  };
}

/**
 * The thoughts behind the claims, keyed by neuron.
 *
 * A claim the chain disproves - no BrainState from this brain, or one whose
 * fired set does not carry the neuron - is left out, because the collection's
 * promise is a claim verified offchain. A read the node refused is retried, at
 * most `attempts` rounds `spacingMs` apart, so throttling costs time and never
 * a card.
 *
 * ponytail: one receipt read per claim, one claim at a time. The registry has
 * single digits of rows today; if a hatch becomes a habit, batch the receipts
 * through the provider rather than opening one read per row.
 */
export async function readClaimThoughts(
  claims: readonly RegistryClaim[],
  brain: string,
  options: ThoughtReadOptions = {},
): Promise<Map<number, ClaimThought>> {
  const sleep = options.sleep ?? sleepMs;
  const spacingMs = options.spacingMs ?? 4_000;
  const attempts = options.attempts ?? 4;
  const read = options.read ?? ((claim: RegistryClaim) => readClaimThoughtOnce(claim, brain));

  const thoughts = new Map<number, ClaimThought>();
  let pending = [...claims];

  for (let attempt = 1; pending.length > 0 && attempt <= attempts; attempt += 1) {
    const refused: RegistryClaim[] = [];

    for (const claim of pending) {
      try {
        const thought = await read(claim);

        if (thought !== null) thoughts.set(claim.neuron, thought);
      } catch {
        refused.push(claim);
      }
    }

    pending = refused;

    if (pending.length > 0 && attempt < attempts) await sleep(spacingMs);
  }

  return thoughts;
}
